import { Divider, Group, Stack } from '@mantine/core';
import type { DatesRangeValue } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { PageLayout } from '../../components/page-layout/page-layout';
import { getCurrency } from '../../helpers/currency-helpers';
import { safeParseDateInput } from '../../helpers/date-time-helpers';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useStatusLabelResolver } from '../../contexts/status-labels.context';
import { useGlobalStore } from '../../store/global-store/global-store';
import { ProcessStoreProvider } from '../../store/process-store/process-store-provider';
import { resetProcessStore, useProcessSelector, useProcessStore } from '../../store/process-store/process-store.hooks';
import { secondaryBase, skyDark } from '../../styles/design-tokens';
import { Button } from '../../ui-library/button/button';
import { Icon } from '../../ui-library/icon/icon';
import { H2, Text } from '../../ui-library/typography/typography';
import { useFlora } from '../flora/flora.context';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { StyledDatePickerInput } from '../process-analysis/header/date-range-picker';
import type { TaskRecord } from '../tasks-table/tasks-table.type';
import { TaskDetails } from '../work-periods/task-detail/task-details';
import { TaskSideSection } from '../work-periods/task-detail/task-side-section';
import { EventGraph } from './event-graph/event-graph';
import { ProcessFilterPanel } from './filters/process-filter-panel';
import { ProcessGraph } from './process-graph/process-graph';
import { getProcessFloraContext } from './process-main.helpers';
import { useProcessViewData } from './process-main.hooks';
import { ProcessOverview } from './process-overview';
import { ProcessTabs } from './process-tabs';
import {
  getEventEdgeTaskDetailTitle,
  getProcessWorkflowTaskDetailTitle,
  getScopedSubprojectIds,
} from './process.helpers';
import { useProcessScope } from './process.hooks';
import { ViewType } from './process.type';

// Create a separate component to use the context
const ProcessContent = ({
  expandTaskDetails,
  toggleExpandTaskDetails,
  setCurrentStartDate,
  setCurrentEndDate,
  setCurrentRightStartDate,
  setCurrentRightEndDate,
}: {
  expandTaskDetails: boolean;
  toggleExpandTaskDetails: () => void;
  setCurrentStartDate: (startDate: Date | null) => void;
  setCurrentEndDate: (endDate: Date | null) => void;
  setCurrentRightStartDate: (startDate: Date | null) => void;
  setCurrentRightEndDate: (endDate: Date | null) => void;
}) => {
  const i18n = useTranslation();
  const [viewType, setViewType] = useState<ViewType>(ViewType.Count);
  const { setContextualInfo } = useFlora();
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  const { scope: leftScope, rightScope, isComparisonView } = useProcessStore();

  // Per-side resolvers so the task-details title matches the casing of the node
  // the user clicked. Derived from each side's whole scope, since a workflow- or
  // team-scoped page has no single board. Same query keys the graphs already
  // use, so these are cache reads rather than extra requests.
  const leftScopedSubprojectIds = useMemo(() => getScopedSubprojectIds(leftScope), [leftScope]);
  const rightScopedSubprojectIds = useMemo(() => getScopedSubprojectIds(rightScope), [rightScope]);
  const { getStatusLabel: resolveLeftStatusLabelForDisplay } = useStatusLabelResolver(leftScopedSubprojectIds);
  const { getStatusLabel: resolveRightStatusLabelForDisplay } = useStatusLabelResolver(rightScopedSubprojectIds);

  // Sync dates from the persisted store to parent component state on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only running on mount
  useEffect(() => {
    // Initialize left dates from store
    const leftStart = safeParseDateInput(leftScope.startDate)?.toDate() ?? null;
    const leftEnd = safeParseDateInput(leftScope.endDate)?.toDate() ?? null;
    setCurrentStartDate(leftStart);
    setCurrentEndDate(leftEnd);

    // Initialize right dates from store
    const rightStart = safeParseDateInput(rightScope.startDate)?.toDate() ?? null;
    const rightEnd = safeParseDateInput(rightScope.endDate)?.toDate() ?? null;
    setCurrentRightStartDate(rightStart);
    setCurrentRightEndDate(rightEnd);
  }, []);

  useEffect(() => {
    if (leftScope && rightScope) {
      setContextualInfo(getProcessFloraContext(leftScope, rightScope, isComparisonView));
    }
  }, [leftScope, rightScope, isComparisonView, setContextualInfo]);

  // Use the restructured hook data
  const { left, right, isLoading, isLoadingTasks } = useProcessViewData();

  // Helper function to render a main section (graph or event graph)
  const renderSection = (side: typeof left | typeof right) => {
    return side.scope.team === null && side.scope.workflow === null ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Stack align="center" justify="center" gap={8}>
          <Icon name="info" color={skyDark} size={36} />
          <Text weight="bold" size="large" color={skyDark}>
            {`Select a ${i18n.t('common.project')}`}
          </Text>
        </Stack>
      </div>
    ) : side.eventScope.showEventGraph ? (
      <EventGraph
        viewType={viewType}
        currentEventScope={side.eventScope}
        setCurrentEventScope={side.setEventScope}
        isRightGraph={side === right}
      />
    ) : (
      <ProcessGraph
        viewType={viewType}
        graphData={side.graphData}
        mapping={side.mapping}
        scope={side.scope}
        setScope={side.setScope}
        stages={side.stages}
        currencyCode={currencyCode}
        isRightGraph={side === right}
      />
    );
  };

  // Get tasks data for the task details panel
  const activeWorkflow = left.workflow || right.workflow;
  const activeEventEdge = left.eventScope.selectedEdgeId || right.eventScope.selectedEdgeId;
  const isWorkflowActive = Boolean(activeWorkflow?.mapping || activeEventEdge);
  const tasks = activeEventEdge
    ? left.eventTasks.length > 0
      ? left.eventTasks
      : right.eventTasks
    : left.tasks.length > 0
      ? left.tasks
      : right.tasks;

  const activeResolvedLabels = activeWorkflow === right.workflow ? right.mapping?.labels : left.mapping?.labels;
  const tasksTitle = activeWorkflow
    ? getProcessWorkflowTaskDetailTitle(
        activeWorkflow,
        activeWorkflow === right.workflow,
        isComparisonView,
        i18n.t,
        activeResolvedLabels,
      )
    : activeEventEdge
      ? getEventEdgeTaskDetailTitle(
          activeEventEdge,
          activeEventEdge === right.eventScope.selectedEdgeId,
          isComparisonView,
          i18n.t,
          activeEventEdge === right.eventScope.selectedEdgeId
            ? resolveRightStatusLabelForDisplay
            : resolveLeftStatusLabelForDisplay,
        )
      : '';

  const memoizedLeftTaskTypes = useMemo(() => left.graphData?.task_types ?? [], [left.graphData?.task_types]);
  const memoizedRightTaskTypes = useMemo(() => right.graphData?.task_types ?? [], [right.graphData?.task_types]);

  return (
    <>
      <BrandedLoadingOverlay variant="colored" visible={isLoading} />
      {expandTaskDetails ? (
        <TaskDetails
          tasks={tasks as TaskRecord[]}
          title={`(${tasks.length}) ${tasksTitle}`}
          onBack={() => toggleExpandTaskDetails()}
        />
      ) : (
        <PageLayout
          controlBar={null}
          tabs={<ProcessTabs viewType={viewType} setViewType={setViewType} />}
          overview={
            isComparisonView
              ? undefined
              : {
                  title: `${i18n.t('navigation.process')} Overview`,
                  content: <ProcessOverview graphData={left.graphData} resolvedLabels={left.mapping?.labels} />,
                }
          }
          sideFilters={{
            content: (
              <ProcessFilterPanel leftTaskTypes={memoizedLeftTaskTypes} rightTaskTypes={memoizedRightTaskTypes} />
            ),
          }}
          mainSection={renderSection(left)}
          secondMainSection={isComparisonView ? renderSection(right) : undefined}
          sideSection={
            <TaskSideSection
              tasks={tasks as TaskRecord[]}
              categoryTitle={tasksTitle}
              onClose={() => {
                left.setWorkflow(null);
                right.setWorkflow(null);
                left.setEventScope({
                  ...left.eventScope,
                  selectedEdgeId: null,
                });
                right.setEventScope({
                  ...right.eventScope,
                  selectedEdgeId: null,
                });
              }}
              isLoading={isLoadingTasks}
              onExpandTaskDetails={toggleExpandTaskDetails}
            />
          }
          showSideSection={isWorkflowActive}
        />
      )}
    </>
  );
};

// Component to watch for portfolio changes and trigger store reset
const usePortfolioWatcher = () => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const prevPortfolioIdRef = useRef<string | null>(portfolio?.id || null);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (prevPortfolioIdRef.current !== null && portfolio?.id !== prevPortfolioIdRef.current) {
      // Reset the store
      resetProcessStore();

      // Force a re-render of the provider by changing its key
      setResetKey((prev) => prev + 1);
    }

    prevPortfolioIdRef.current = portfolio?.id || null;
  }, [portfolio?.id]);

  return { resetKey };
};

export function Process() {
  const i18n = useTranslation();
  useDocumentTitle('Process Map - Bloomfilter');
  const { team, workflow, board, useWorkflow } = useProcessScope();
  // Read isComparisonView and setters from Zustand store (same source as filter panel and graphs)
  const isComparisonView = useProcessSelector((state) => state.isComparisonView);
  const { scope, setScope, setRightScope, rightScope } = useProcessStore();

  // Get the reset key from the portfolio watcher
  const { resetKey } = usePortfolioWatcher();

  // Local state for left date picker
  const [leftStartDate, setLeftStartDate] = useState<Date | null>(null);
  const [leftEndDate, setLeftEndDate] = useState<Date | null>(null);
  const [isLeftDatesDirty, setIsLeftDatesDirty] = useState(false);

  // Local state for right date picker
  const [rightStartDate, setRightStartDate] = useState<Date | null>(null);
  const [rightEndDate, setRightEndDate] = useState<Date | null>(null);
  const [isRightDatesDirty, setIsRightDatesDirty] = useState(false);

  const [expandTaskDetails, { toggle: toggleExpandTaskDetails }] = useDisclosure(false);

  // Local state for maintaining the current applied dates for the provider
  const [currentStartDate, setCurrentStartDate] = useState<string | null>(null);
  const [currentEndDate, setCurrentEndDate] = useState<string | null>(null);

  // One-time URL parameter initialization for quick links from Flora artifacts
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitFromUrlRef = useRef(false);
  const teams = useGlobalStore((state) => state.teams);

  useEffect(() => {
    if (didInitFromUrlRef.current) return;

    const urlTeamId = searchParams.get('teamId');
    const urlBoardId = searchParams.get('boardId');
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlUseGroups = searchParams.get('useGroups');

    // No URL params to process
    if (!urlTeamId && !urlStartDate && !urlEndDate) {
      didInitFromUrlRef.current = true;
      return;
    }

    // Wait for teams to be loaded before resolving IDs to objects
    if (!teams || teams.length === 0) return;

    const scopeUpdate: Record<string, unknown> = {};

    if (urlTeamId) {
      const resolvedTeam = teams.find((t) => t.id === urlTeamId);
      if (resolvedTeam) {
        scopeUpdate.team = resolvedTeam;
        // Clear dependent filters when navigating via URL
        scopeUpdate.epic = null;
        scopeUpdate.task = null;
        scopeUpdate.taskTypes = null;
        scopeUpdate.workPeriod = null;

        if (urlBoardId) {
          const resolvedBoard = resolvedTeam.subprojects.find((b) => b.id === urlBoardId);
          if (resolvedBoard) {
            scopeUpdate.board = resolvedBoard;
          }
        } else {
          // Explicitly clear the board when team is set via URL without a board
          scopeUpdate.board = null;
        }
      }
    }

    if (urlStartDate) {
      scopeUpdate.startDate = urlStartDate;
      const parsedStart = safeParseDateInput(urlStartDate)?.toDate() ?? null;
      setLeftStartDate(parsedStart);
      setCurrentStartDate(urlStartDate);
    }

    if (urlEndDate) {
      scopeUpdate.endDate = urlEndDate;
      const parsedEnd = safeParseDateInput(urlEndDate)?.toDate() ?? null;
      setLeftEndDate(parsedEnd);
      setCurrentEndDate(urlEndDate);
    }

    if (urlUseGroups !== null) {
      scopeUpdate.useGroups = urlUseGroups === 'true';
    }

    if (Object.keys(scopeUpdate).length > 0) {
      setScope(scopeUpdate);
    }

    didInitFromUrlRef.current = true;
    // Preserve workPeriodIds for the filter panel to handle once work periods load
    const urlWorkPeriodIds = searchParams.get('workPeriodIds');
    const remaining = new URLSearchParams();
    if (urlWorkPeriodIds) {
      remaining.set('workPeriodIds', urlWorkPeriodIds);
    }
    setSearchParams(remaining, { replace: true });
  }, [searchParams, setSearchParams, teams, setScope]);

  // Track previous comparison mode to detect when entering comparison mode
  const prevIsComparisonView = useRef(isComparisonView);

  // Sync right date picker with store when entering comparison mode
  useEffect(() => {
    if (isComparisonView && !prevIsComparisonView.current) {
      // Entering comparison mode - read dates from rightScope in store
      // If rightScope has dates, use them; otherwise fall back to left dates
      const storedRightStart = safeParseDateInput(rightScope.startDate)?.toDate() ?? null;
      const storedRightEnd = safeParseDateInput(rightScope.endDate)?.toDate() ?? null;

      if (storedRightStart && storedRightEnd) {
        setRightStartDate(storedRightStart);
        setRightEndDate(storedRightEnd);
      } else {
        // No stored right dates - copy from left
        setRightStartDate(leftStartDate);
        setRightEndDate(leftEndDate);
      }
      setIsRightDatesDirty(false);
    }
    prevIsComparisonView.current = isComparisonView;
  }, [isComparisonView, leftStartDate, leftEndDate, rightScope.startDate, rightScope.endDate]);

  // Handle left date range selection
  const handleLeftDateRangeChange = useCallback((dates: DatesRangeValue) => {
    const [start, end] = dates || [null, null];
    const startDate = start ? safeParseDateInput(start)?.toDate() : null;
    const endDate = end ? safeParseDateInput(end)?.toDate() : null;
    setLeftStartDate(startDate ?? null);
    setLeftEndDate(endDate ?? null);
    setIsLeftDatesDirty(true);
  }, []);

  // Handle right date range selection
  const handleRightDateRangeChange = useCallback((dates: DatesRangeValue) => {
    const [start, end] = dates || [null, null];
    const startDate = start ? safeParseDateInput(start)?.toDate() : null;
    const endDate = end ? safeParseDateInput(end)?.toDate() : null;
    setRightStartDate(startDate ?? null);
    setRightEndDate(endDate ?? null);
    setIsRightDatesDirty(true);
  }, []);

  // Apply the left dates to the scope when Apply button is clicked
  const handleApplyLeftDates = useCallback(() => {
    const formattedStartDate = leftStartDate ? dayjs(leftStartDate).toISOString() : null;
    const formattedEndDate = leftEndDate ? dayjs(leftEndDate).toISOString() : null;

    // Update our local state for the Provider's initialScope
    setCurrentStartDate(formattedStartDate);
    setCurrentEndDate(formattedEndDate);

    // Clear work period selection when dates are manually changed
    setScope({ workPeriod: null, startDate: formattedStartDate, endDate: formattedEndDate });

    setIsLeftDatesDirty(false);
  }, [leftStartDate, leftEndDate, setScope]);

  // Apply the right dates to the scope when Apply button is clicked
  const handleApplyRightDates = useCallback(() => {
    const formattedStartDate = rightStartDate ? dayjs(rightStartDate).toISOString() : null;
    const formattedEndDate = rightEndDate ? dayjs(rightEndDate).toISOString() : null;

    // Clear work period selection when dates are manually changed
    setRightScope({ workPeriod: null, startDate: formattedStartDate, endDate: formattedEndDate });

    setIsRightDatesDirty(false);
  }, [rightStartDate, rightEndDate, setRightScope]);

  // Date range formatter helper for date picker display
  const dateRangeFormatter = useCallback(
    ({ date }: { date: unknown }) => {
      const isValidRange = Array.isArray(date) && date.every((d: Date | null) => d && dayjs(d).isValid());

      if (isValidRange) {
        return i18n.t('process_analysis.date_range.format', {
          startDate: dayjs(date[0]).format('MM/DD/YYYY'),
          endDate: dayjs(date[1]).format('MM/DD/YYYY'),
        });
      }

      return '';
    },
    [i18n.t],
  );

  const header = (
    <Group>
      <Icon name="map" color={secondaryBase} size={36} />
      <H2>{`${i18n.t('navigation.process')} ${expandTaskDetails ? i18n.t('common.work_units') : ''}`}</H2>

      {!expandTaskDetails &&
        (isComparisonView ? (
          <Group gap="md" align="flex-end">
            {/* Left Date Picker */}
            <Group gap="xs">
              <Text size="medium" weight="bold">
                Left
              </Text>

              <StyledDatePickerInput
                type="range"
                value={[leftStartDate, leftEndDate]}
                onChange={handleLeftDateRangeChange}
                placeholder="Select date range"
                maxDate={dayjs().toDate()}
                valueFormatter={dateRangeFormatter}
                allowSingleDateInRange={false}
                rightSection={<Icon name="unfold_more" size={16} color={skyDark} />}
                rightSectionPointerEvents="none"
                disabled={!!scope.workPeriod}
              />
              <Button
                onClick={handleApplyLeftDates}
                variant="secondary"
                radius="xl"
                size="xs"
                disabled={!isLeftDatesDirty || !!scope.workPeriod}
                style={{ border: !isLeftDatesDirty || scope.workPeriod ? `1px solid ${skyDark}` : 'none' }}
              >
                Apply
              </Button>
            </Group>

            <Divider orientation="vertical" />

            {/* Right Date Picker */}
            <Group gap="xs">
              <Text size="medium" weight="bold">
                Right
              </Text>

              <StyledDatePickerInput
                type="range"
                value={[rightStartDate, rightEndDate]}
                onChange={handleRightDateRangeChange}
                placeholder="Select date range"
                maxDate={dayjs().toDate()}
                valueFormatter={dateRangeFormatter}
                allowSingleDateInRange={false}
                rightSection={<Icon name="unfold_more" size={16} color={skyDark} />}
                rightSectionPointerEvents="none"
                disabled={!!rightScope.workPeriod}
              />
              <Button
                onClick={handleApplyRightDates}
                variant="secondary"
                radius="xl"
                size="xs"
                disabled={!isRightDatesDirty || !!rightScope.workPeriod}
                style={{ border: !isRightDatesDirty || rightScope.workPeriod ? `1px solid ${skyDark}` : 'none' }}
              >
                Apply
              </Button>
            </Group>
          </Group>
        ) : (
          <>
            <StyledDatePickerInput
              type="range"
              value={[leftStartDate, leftEndDate]}
              onChange={handleLeftDateRangeChange}
              placeholder="Select date range"
              maxDate={dayjs().toDate()}
              valueFormatter={dateRangeFormatter}
              allowSingleDateInRange={false}
              rightSection={<Icon name="unfold_more" size={16} color={skyDark} />}
              rightSectionPointerEvents="none"
              disabled={!!scope.workPeriod}
            />
            <Button
              onClick={handleApplyLeftDates}
              variant="secondary"
              radius="xl"
              size="xs"
              disabled={!isLeftDatesDirty || !!scope.workPeriod}
              style={{ border: !isLeftDatesDirty || scope.workPeriod ? `1px solid ${skyDark}` : 'none' }}
            >
              Apply Dates
            </Button>
          </>
        ))}
    </Group>
  );

  // Create a unique key for the ProcessStoreProvider to force remounting
  // Now also include the resetKey to force remount when portfolio changes
  const providerKey = useMemo(
    () =>
      `process-provider-${resetKey}-${useWorkflow ? 'workflow' : 'team'}-${useWorkflow ? workflow?.id || 'no-workflow' : team?.id || 'no-team'}-${board?.id || 'no-board'}`,
    [team?.id, board?.id, workflow?.id, useWorkflow, resetKey],
  );

  // Create stable references for the initial scopes to prevent unnecessary re-renders
  const initialScope = useMemo(
    () => ({
      startDate: currentStartDate,
      endDate: currentEndDate,
    }),
    [currentStartDate, currentEndDate],
  );

  const initialRightScope = useMemo(
    () => ({
      startDate: currentStartDate,
      endDate: currentEndDate,
    }),
    [currentStartDate, currentEndDate],
  );

  return (
    <ProcessStoreProvider key={providerKey} initialScope={initialScope} initialRightScope={initialRightScope}>
      <MenuDrivenContainer header={header}>
        <ProcessContent
          expandTaskDetails={expandTaskDetails}
          toggleExpandTaskDetails={toggleExpandTaskDetails}
          setCurrentStartDate={setLeftStartDate}
          setCurrentEndDate={setLeftEndDate}
          setCurrentRightStartDate={setRightStartDate}
          setCurrentRightEndDate={setRightEndDate}
        />
      </MenuDrivenContainer>
    </ProcessStoreProvider>
  );
}
